import * as React from 'react';
import { connect } from 'react-redux';
import { Accordion, Button, ProgressBar } from 'react-bootstrap';
import * as actions from './actions/PhotographerUploadActions';
import notify from '../../shared/feedback/notifications';
import OrderRow from './list/OrderRow';
import { handleError } from '../../shared/ErrorCatching';
import { IOrder, IOrderRowMetadata, IFileMetadata, ICategoryMetadata } from './Entities';
import { Translate } from '../../shared/LanguageService';
import Loader from 'react-loader';

class PhotographerUploadPage extends React.Component<any, any> {
    constructor(props: any, state: any) {
        super(props, state);

        this.state = {
            upload: false,
            uploadProgress: 0,
            totalBytes: 0,
            progressObject: {},
            metadataList: [],
            gatheringMetadata: false,
            dragging: false,
            completedOrderRows: [],
            processedOrderRows: [],
            orderRowIdsToUpload: [],
            uploadedOrderRowIds: []
        };

        this.startUpload = this.startUpload.bind(this);
        this.setTotalBytes = this.setTotalBytes.bind(this);
        this.updateProgress = this.updateProgress.bind(this);
        this.updateTotalProgress = this.updateTotalProgress.bind(this);
        this.addFileToMetadataList = this.addFileToMetadataList.bind(this);
        this.removeFromMetadataList = this.removeFromMetadataList.bind(this);
        this.toggleDragging = this.toggleDragging.bind(this);
        this.setOrderRowIdUploadDone = this.setOrderRowIdUploadDone.bind(this);
        this.setOrderRowIdAllFilesProcessed = this.setOrderRowIdAllFilesProcessed.bind(this);
        this.onUploadDone = this.onUploadDone.bind(this);
        this.startUploadingNextOrderRow = this.startUploadingNextOrderRow.bind(this);
    }

    componentDidMount() {
        this.props.actions.listPhotographerUploads().catch(handleError);
    }

    updateProgress(bytes: number, fileName: string, orderRowId: number, type: number) {
        let progressObject = this.state.progressObject;
        let orderRowProgress = progressObject[orderRowId.toString()];

        if (!orderRowProgress) {
            progressObject[orderRowId.toString()] = {};
            progressObject[orderRowId.toString()][type.toString()] = {};
        } else if (!orderRowProgress[type.toString()]) {
            progressObject[orderRowId.toString()][type.toString()] = {};
        }

        progressObject[orderRowId.toString()][type.toString()][fileName] = bytes;

        this.setState({ progressObject: progressObject });
        this.updateTotalProgress(progressObject);
    }

    updateTotalProgress(progressObject: any) {
        let progress = 0;

        for (let orderRow in progressObject) {
            for (let type in progressObject[orderRow]) {
                for (let file in progressObject[orderRow][type]) {
                    progress += progressObject[orderRow][type][file];
                }
            }
        }

        let progressPercentage = Math.min(
            parseInt(((progress / this.state.totalBytes) * 100).toFixed()),
            100
        );
        this.setState({
            uploadProgress: progressPercentage,
            upload: progressPercentage !== 100
        });
    }

    onUploadDone(anyUploads: boolean) {
        if (anyUploads) {
            notify.success(Translate('photographer-uploads/notification/upload-done'));
            this.setState({ gatheringMetadata: false, metadataList: [] });
        }
    }

    toggleDragging(dragging: boolean) {
        this.setState({ dragging: dragging });
    }

    setTotalBytes(bytes: number) {
        let totalBytes = this.state.totalBytes;
        totalBytes += bytes;
        this.setState({ totalBytes: totalBytes });
    }

    startUpload() {
        this.props.actions
            .saveMetadata(this.state.metadataList)
            .then(() => {
                this.setState((prevState) => {
                    let orderRowIdsToUpload = prevState.metadataList.map(
                        (metadata) => metadata.orderRowId
                    );

                    return {
                        uploadedOrderRowIds:
                            orderRowIdsToUpload.length > 0 ? [orderRowIdsToUpload[0]] : [],
                        orderRowIdsToUpload
                    };
                });
            })
            .catch(handleError);
    }

    addFileToMetadataList(orderRowId: number, category: number, fileName: string) {
        this.setState({ uploadProgress: 0 });
        let metadataList = this.state.metadataList;

        let orderRowMetadataIndex = metadataList.findIndex(
            (x: IOrderRowMetadata) => x.orderRowId === orderRowId
        );
        if (orderRowMetadataIndex > -1) {
            let categoryMetadataIndex = metadataList[
                orderRowMetadataIndex
            ].categoryMetadatas.findIndex((x: ICategoryMetadata) => x.category === category);
            if (categoryMetadataIndex > -1) {
                let fileMetadatas = metadataList[orderRowMetadataIndex].categoryMetadatas[
                    categoryMetadataIndex
                ].fileMetadatas.push({
                    filename: fileName
                });
            } else {
                let fileMetadata: IFileMetadata = {
                    filename: fileName
                };

                let categoryMetadata: ICategoryMetadata = {
                    category: category,
                    fileMetadatas: [fileMetadata]
                };

                metadataList[orderRowMetadataIndex].categoryMetadatas.push(categoryMetadata);
            }
        } else {
            let fileMetadata: IFileMetadata = {
                filename: fileName
            };

            let categoryMetadata: ICategoryMetadata = {
                category: category,
                fileMetadatas: [fileMetadata]
            };

            let metadataObj: IOrderRowMetadata = {
                orderRowId: orderRowId,
                categoryMetadatas: [categoryMetadata]
            };

            metadataList.push(metadataObj);
        }

        this.setState({ metadataList: metadataList });
    }

    removeFromMetadataList(orderRowId: number, category: number, fileName: string) {
        let metadataList = this.state.metadataList;
        if (metadataList && metadataList.length > 0) {
            let orderRowIndex = metadataList.findIndex(
                (x: IOrderRowMetadata) => x.orderRowId === orderRowId
            );
            let categoryIndex = metadataList[orderRowIndex].categoryMetadatas.findIndex(
                (x: ICategoryMetadata) => x.category === category
            );
            let fileIndex = metadataList[orderRowIndex].categoryMetadatas[
                categoryIndex
            ].fileMetadatas.findIndex((x: IFileMetadata) => x.filename === fileName);

            metadataList[orderRowIndex].categoryMetadatas[categoryIndex].fileMetadatas =
                metadataList[orderRowIndex].categoryMetadatas[categoryIndex].fileMetadatas.filter(
                    (x: IFileMetadata) => x.filename !== fileName
                );

            if (
                metadataList[orderRowIndex].categoryMetadatas[categoryIndex].fileMetadatas
                    .length === 0
            ) {
                metadataList[orderRowIndex].categoryMetadatas = metadataList[
                    orderRowIndex
                ].categoryMetadatas.filter((x: ICategoryMetadata) => x.category !== category);
            }

            this.setState({ metadataList: metadataList });
        }
    }

    setOrderRowIdUploadDone(id: number) {
        let completedOrderRows = this.state.completedOrderRows;
        completedOrderRows.push(id);
        this.setState({ completedOrderRows: completedOrderRows });
    }

    setOrderRowIdAllFilesProcessed(id: number) {
        let processedOrderRows = this.state.processedOrderRows;
        processedOrderRows.push(id);
        this.setOrderRowIdUploadDone(id);
        this.setState({ processedOrderRows }, () => this.startUploadingNextOrderRow());
    }

    startUploadingNextOrderRow() {
        let shouldContinue = true;

        this.state.orderRowIdsToUpload.forEach((orderRowId) => {
            if (!this.state.uploadedOrderRowIds.includes(orderRowId) && shouldContinue) {
                let uploadedOrderRowIds = this.state.uploadedOrderRowIds;
                uploadedOrderRowIds.push(orderRowId);
                this.setState({ uploadOrderRow: uploadedOrderRowIds });
                shouldContinue = false;
            }
        });
    }

    render() {
        return (
            <div
                className={`photographer-upload-container ${
                    this.state.dragging ? `dragging-active` : ''
                }`}
            >
                {this.state.gatheringMetadata && (
                    <div className="loader-container">
                        <h3 className="loader-text">
                            {Translate(
                                'photographer-uploads/page/gathering-data-and-loading-preview'
                            )}
                        </h3>
                        <Loader loaded={false}></Loader>
                    </div>
                )}
                <ProgressBar
                    active={this.state.upload}
                    now={this.state.uploadProgress}
                    label={`${Translate('photographer-uploads/page/uploading')}: ${
                        this.state.uploadProgress
                    }%`}
                    max={100}
                    bsStyle="success"
                />
                <Button
                    onClick={() => this.startUpload()}
                    bsSize="large"
                    bsStyle="info"
                    className="upload-button"
                    disabled={
                        this.state.uploadProgress !== 0 ||
                        this.state.totalBytes === 0 ||
                        this.state.gatheringMetadata
                    }
                >
                    <i className="material-icons">cloud_upload</i>{' '}
                    {Translate('photographer-uploads/page/button-label/upload')}
                </Button>
                <div className={`upload-panel ${this.state.gatheringMetadata ? 'waiting' : ''}`}>
                    <Accordion className="list-group order-list">
                        {this.props.list.orders &&
                            this.props.list.orders.map((x: IOrder) => {
                                return (
                                    <OrderRow
                                        updateProgress={this.updateProgress}
                                        updateTotalBytes={this.setTotalBytes}
                                        upload={this.state.upload}
                                        order={x}
                                        key={x.id}
                                        addFileToMetadataList={this.addFileToMetadataList}
                                        removeFileFromMetadataList={this.removeFromMetadataList}
                                        toggleDragging={this.toggleDragging}
                                        completedOrderRows={this.state.completedOrderRows}
                                        setOrderRowIdUploadDone={this.setOrderRowIdUploadDone}
                                        processedOrderRows={this.state.processedOrderRows}
                                        setOrderRowIdAllFilesProcessed={
                                            this.setOrderRowIdAllFilesProcessed
                                        }
                                        onUploadDone={this.onUploadDone}
                                        uploadedOrderRowIds={this.state.uploadedOrderRowIds}
                                        user={this.props.user}
                                    />
                                );
                            })}
                    </Accordion>
                </div>
            </div>
        );
    }
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            listPhotographerUploads: () => dispatch(actions.listPhotographerUploads()),
            saveMetadata: (metadataList: IOrderRowMetadata[]) =>
                dispatch(actions.savePhotographerUploadsMetadata(metadataList))
        }
    };
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        list: state.photographerUploads.list
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(PhotographerUploadPage);
